import { Choice, SceneData, DialogueHistoryEntry } from '../types/index';
import { GAME_CONFIG, MODELS } from '../config';
import './NotificationBar';

export class GameUI {
    private storyText: HTMLElement;
    private choicesContainer: HTMLElement;
    private loadingElement: HTMLElement;
    private dialogueHistory: HTMLElement;
    private exportButton: HTMLButtonElement;
    private importButton: HTMLButtonElement;
    private importFile: HTMLInputElement;
    private notificationBar: HTMLElement & { requestUpdate?: () => void };
    private currentModel: string = GAME_CONFIG.defaultModel;

    constructor() {
        this.storyText = document.getElementById('story-text') as HTMLElement;
        this.choicesContainer = document.getElementById('choices') as HTMLElement;
        this.loadingElement = document.getElementById('loading') as HTMLElement;
        this.dialogueHistory = document.getElementById('dialogue-history') as HTMLElement;
        this.exportButton = document.getElementById('export-history') as HTMLButtonElement;
        this.importButton = document.getElementById('import-history') as HTMLButtonElement;
        this.importFile = document.getElementById('import-file') as HTMLInputElement;

        // 创建通知条组件
        this.notificationBar = document.createElement('notification-bar') as (HTMLElement & { requestUpdate?: () => void });
        // 确保通知栏有一个初始的空数组
        (this.notificationBar as any).notifications = [];
        document.body.insertBefore(this.notificationBar, document.body.firstChild);

        if (!this.exportButton || !this.importButton || !this.importFile || !this.dialogueHistory) {
            console.error('无法找到必要的 DOM 元素');
            return;
        }

        this.initializeHistoryControls();
    }

    private initializeHistoryControls(): void {
        // 创建按钮容器
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'header-controls';

        // 获取重新开始按钮
        const restartButton = document.getElementById('restart-game');
        if (restartButton) {
            restartButton.className = 'game-button restart-button';
            restartButton.innerHTML = `
                <span class="button-content">
                    <svg class="icon" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                    </svg>
                    <span>重新开始</span>
                </span>
            `;

            // 将重新开始按钮插入到游戏标题下方
            const gameHeader = document.querySelector('.game-header');
            if (gameHeader) {
                gameHeader.after(restartButton);
            }
        }

        // 添加分享按钮
        const shareButton = document.createElement('button');
        shareButton.className = 'game-button';
        shareButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                </svg>
                <span>分享</span>
            </span>
        `;

        // 导出按钮
        this.exportButton.className = 'game-button';
        this.exportButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2v9.67z"/>
                </svg>
                <span>导出存档</span>
            </span>
        `;

        // 导入按钮
        this.importButton.className = 'game-button';
        this.importButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6-3.33l2.59 2.58L17 12.67l-5-5-5 5 1.41 1.41L11 11.67V21h2v-9.33z"/>
                </svg>
                <span>导入存档</span>
            </span>
        `;

        // 更新日志按钮
        const changelogButton = document.createElement('button');
        changelogButton.className = 'game-button';
        changelogButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
                <span>更新反馈</span>
            </span>
        `;

        // 添加按钮到容器
        buttonContainer.appendChild(shareButton);
        buttonContainer.appendChild(this.exportButton);
        buttonContainer.appendChild(this.importButton);
        buttonContainer.appendChild(changelogButton);

        // 将按钮容器添加到游戏容器
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            // 在游戏容器的开头插入按钮容器
            gameContainer.insertBefore(buttonContainer, gameContainer.firstChild);
        }

        // 更新日志按钮点击事件
        changelogButton.addEventListener('click', () => {
            window.open('https://v7vlqijlqd.feishu.cn/wiki/A9eKwS7agicX22kdKkVcSN9Kncf?from=from_copylink', '_blank');
        });

        // 分享按钮点击事件
        shareButton.addEventListener('click', () => {
            this.handleShare();
        });

        // 导入按钮点击事件
        this.importButton.addEventListener('click', () => {
            this.importFile.click();
        });

        // 重新开始按钮点击事件
        if (restartButton) {
            restartButton.addEventListener('click', () => {
                const event = new CustomEvent('restart-game');
                document.dispatchEvent(event);
            });
        }

        // 文件选择事件
        this.importFile.addEventListener('change', (event) => {
            const file = this.importFile.files?.[0];
            
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const content = e.target?.result as string;
                        const data = JSON.parse(content);
                        const importEvent = new CustomEvent('import-history', {
                            detail: { data }
                        });
                        document.dispatchEvent(importEvent);
                    } catch (error) {
                        console.error('导入存档失败:', error);
                        this.showNotification('导入存档失败！请确保文件格式正确。', 'error');
                    }
                };
                reader.readAsText(file);
            }
            
            // 清除文件选择，允许重复选择同一文件
            this.importFile.value = '';
        });
    }

    private async handleShare(): Promise<void> {
        const shareImage = 'https://img.picgo.net/2025/03/09/pstc8a775de4763c908.jpeg';
        
        // 检测是否在微信浏览器中
        const isWechat = /MicroMessenger/i.test(navigator.userAgent);
        
        if (isWechat) {
            // 创建遮罩层，显示"请长按图片保存"的提示
            const overlay = document.createElement('div');
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                z-index: 9999;
            `;
            
            const img = document.createElement('img');
            img.src = shareImage;
            img.style.maxWidth = '80%';
            img.style.maxHeight = '60%';
            
            const tip = document.createElement('div');
            tip.textContent = '请长按图片保存';
            tip.style.cssText = `
                color: white;
                margin-top: 20px;
                font-size: 16px;
            `;
            
            overlay.appendChild(img);
            overlay.appendChild(tip);
            
            // 点击遮罩层关闭
            overlay.addEventListener('click', () => {
                document.body.removeChild(overlay);
            });
            
            document.body.appendChild(overlay);
        } else {
            try {
                // 显示加载提示，3秒后自动消失
                const loadingNotificationId = Date.now().toString();
                this.showNotification('正在准备下载...', 'info', 3000);
                
                // 获取图片数据
                const response = await fetch(shareImage);
                const blob = await response.blob();
                
                // 创建下载链接
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'share.jpg';
                document.body.appendChild(link);
                
                // 触发下载
                link.click();
                
                // 清理
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
                
                // 显示成功提示，2秒后自动消失
                this.showNotification('图片已开始下载', 'success', 2000);
            } catch (error) {
                console.error('下载图片失败:', error);
                // 显示错误提示，3秒后自动消失
                this.showNotification('下载失败，将显示预览图片', 'error', 3000);
                
                // 如果下载失败，显示图片预览
                const overlay = document.createElement('div');
                overlay.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                `;
                
                const img = document.createElement('img');
                img.src = shareImage;
                img.style.maxWidth = '80%';
                img.style.maxHeight = '60%';
                
                const tip = document.createElement('div');
                tip.textContent = '请右键点击图片并选择"图片另存为"';
                tip.style.cssText = `
                    color: white;
                    margin-top: 20px;
                    font-size: 16px;
                `;
                
                overlay.appendChild(img);
                overlay.appendChild(tip);
                
                // 点击遮罩层关闭
                overlay.addEventListener('click', () => {
                    document.body.removeChild(overlay);
                });
                
                document.body.appendChild(overlay);
            }
        }
    }

    toggleLoading(show: boolean): void {
        if (this.loadingElement) {
            this.loadingElement.style.display = show ? 'flex' : 'none';
        }
    }

    /**
     * 显示通知
     * @param message 通知消息内容
     * @param type 通知类型：
     *   - 'info' (默认) - 蓝色，用于一般信息提示
     *   - 'success' - 绿色，用于成功提示
     *   - 'warning' - 橙色，用于警告提示
     *   - 'error' - 红色，用于错误提示
     * @param duration 通知显示时长（毫秒）
     *   - 不设置：通知会一直显示，需要用户手动关闭
     *   - 设置具体时间：到时间后自动消失
     * 
     * 使用示例：
     * ```typescript
     * // 1. 重要通知，不自动消失
     * ui.showNotification('欢迎来到赛博朋克2077文字版！', 'info');
     * 
     * // 2. 成功提示，2秒后自动消失
     * ui.showNotification('游戏已保存！', 'success', 2000);
     * 
     * // 3. 警告提示，3秒后自动消失
     * ui.showNotification('即将断开连接...', 'warning', 3000);
     * 
     * // 4. 错误提示，3秒后自动消失
     * ui.showNotification('保存失败！', 'error', 3000);
     * 
     * // 建议的使用时长：
     * // - 成功提示：2000ms (2秒)
     * // - 错误/警告：3000ms (3秒)
     * // - 重要通知：不设置 duration（需要用户确认）
     * ```
     */
    showNotification(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration?: number): void {
        const notification = {
            id: Date.now().toString() + Math.random().toString(36).substring(2, 8),
            message,
            type,
            timestamp: Date.now(),
            duration
        };

        // 确保 notifications 是一个数组
        const bar = this.notificationBar as any;
        if (!Array.isArray(bar.notifications)) {
            bar.notifications = [];
        }

        // 添加新通知
        bar.notifications = [...bar.notifications, notification];

        // 强制更新组件
        this.notificationBar.requestUpdate?.();
    }

    createCustomInput(onSubmit: (text: string) => void): HTMLElement {
        const container = document.createElement('div');
        container.className = 'custom-input-container';

        // 添加指引信息
        const inputGuide = document.createElement('div');
        inputGuide.className = 'input-guide';
        inputGuide.textContent = '如果有选项之外的想法，可以在此输入自定义行动或对话...';
        container.appendChild(inputGuide);

        const textarea = document.createElement('textarea');
        textarea.className = 'custom-input';
        textarea.placeholder = '输入你想说的话或要执行的动作...';
        textarea.maxLength = GAME_CONFIG.maxCustomInputLength;
        textarea.rows = 3;

        const charCounter = document.createElement('div');
        charCounter.className = 'char-counter';
        this.updateCharCounter(textarea, charCounter);

        const submitButton = document.createElement('button');
        submitButton.className = 'custom-submit';
        submitButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M3.4 20.4L20.85 12.92c.5-.23.5-.84 0-1.07L3.41 4.3c-.5-.23-.95.27-.67.74l4.13 6.85c.24.37.24.89 0 1.26L2.72 19.5c-.26.48.18.97.68.9z"/>
                </svg>
                <span>确认输入</span>
            </span>
        `;
        submitButton.disabled = true;

        const controls = document.createElement('div');
        controls.className = 'custom-input-controls';

        const handleSubmit = () => {
            const text = textarea.value.trim();
            if (!text) {
                this.showNotification('请输入内容', 'error');
                return;
            }
            onSubmit(text);
        };

        textarea.addEventListener('input', () => {
            this.updateCharCounter(textarea, charCounter);
            submitButton.disabled = !textarea.value.trim() || textarea.value.length < 2;
        });

        // 添加回车发送支持
        textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!submitButton.disabled) {
                    handleSubmit();
                }
            }
        });

        submitButton.addEventListener('click', handleSubmit);

        container.appendChild(textarea);
        container.appendChild(charCounter);
        controls.appendChild(submitButton);
        container.appendChild(controls);

        return container;
    }

    private updateCharCounter(textarea: HTMLTextAreaElement, counter: HTMLElement): void {
        const maxLength = textarea.maxLength;
        const currentLength = textarea.value.length;
        counter.textContent = `${currentLength}/${maxLength}`;
    }

    createChoiceButton(choice: Choice, onClick: () => void): HTMLButtonElement {
        const button = document.createElement('button');
        button.textContent = choice.text;
        button.className = 'choice-button';
        

        button.addEventListener('click', onClick);
        return button;
    }

    createStatusDisplay(inventory: string[], relationships: Record<string, number>): HTMLElement {
        const container = document.createElement('div');
        container.className = 'status-container';
        
        container.innerHTML = `
            <div class="inventory">
                <h3>物品栏</h3>
                <ul>${inventory.map(item => `<li>${item}</li>`).join('')}</ul>
            </div>
            <div class="relationships">
                <h3>NPC关系</h3>
                <ul>${Object.entries(relationships).map(([npc, value]) => 
                    `<li>${npc}: ${value}</li>`).join('')}</ul>
            </div>
        `;

        return container;
    }

    createSaveLoadButtons(onSave: () => void, onLoad: () => void): HTMLElement {
        const container = document.createElement('div');
        container.className = 'save-load-container';
        
        const saveButton = document.createElement('button');
        saveButton.className = 'game-button';
        saveButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V7l-4-4zm2 16H5V5h11.17L19 7.83V19zm-7-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                保存游戏
            </span>
        `;
        
        const loadButton = document.createElement('button');
        loadButton.className = 'game-button';
        loadButton.innerHTML = `
            <span class="button-content">
                <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
                </svg>
                加载游戏
            </span>
        `;
        
        saveButton.addEventListener('click', onSave);
        loadButton.addEventListener('click', onLoad);
        
        container.appendChild(saveButton);
        container.appendChild(loadButton);
        
        return container;
    }

    displayScene(sceneData: SceneData): void {
        // 清空选项容器
        while (this.choicesContainer.firstChild) {
            this.choicesContainer.removeChild(this.choicesContainer.firstChild);
        }
        
        // 显示场景文本
        this.storyText.innerHTML = '';
        
        // 添加场景描述
        const sceneDescription = document.createElement('div');
        sceneDescription.className = 'scene-description';
        sceneDescription.textContent = sceneData.scene_text;
        this.storyText.appendChild(sceneDescription);
        
        // 添加NPC对话（如果有）
        if (sceneData.npc_dialogue) {
            const npcDialogue = document.createElement('div');
            npcDialogue.className = 'npc-dialogue';
            npcDialogue.textContent = sceneData.npc_dialogue;
            this.storyText.appendChild(npcDialogue);
        }
        
        // 滚动到底部
        this.storyText.scrollTop = this.storyText.scrollHeight;
    }

    disableAllInputs(): void {
        const inputs = document.querySelectorAll('button, textarea');
        inputs.forEach(input => {
            if (input instanceof HTMLButtonElement || input instanceof HTMLTextAreaElement) {
                input.disabled = true;
            }
        });
    }

    enableAllInputs(): void {
        const inputs = document.querySelectorAll('button, textarea');
        inputs.forEach(input => {
            if (input instanceof HTMLButtonElement) {
                if (input.classList.contains('custom-submit')) {
                    const textarea = input.parentElement?.querySelector('textarea');
                    input.disabled = !textarea?.value.trim();
                } else {
                    input.disabled = false;
                }
            } else if (input instanceof HTMLTextAreaElement) {
                input.disabled = false;
            }
        });
    }

    showError(message: string): void {
        this.storyText.innerHTML = message;
        this.choicesContainer.innerHTML = `
            <button onclick="location.reload()">重新开始</button>
        `;
    }

    appendToChoices(element: HTMLElement): void {
        if (this.choicesContainer) {
            this.choicesContainer.appendChild(element);
        }
    }

    updateDialogueHistory(entries: DialogueHistoryEntry[]): void {
        try {
            if (!this.dialogueHistory) {
                console.error('对话历史容器不存在');
                return;
            }

            if (!Array.isArray(entries)) {
                console.error('无效的对话历史数据:', entries);
                return;
            }

            console.log('更新对话历史:', entries);

            this.dialogueHistory.innerHTML = entries.map(entry => {
                if (!entry) return '';
                
                return `
                    <div class="dialogue-entry">
                        <div class="timestamp">${new Date(entry.timestamp).toLocaleString()}</div>
                        <div class="scene-name">场景：${entry.scene || '未知场景'}</div>
                        ${entry.scene_description ? 
                            `<div class="scene-description">${entry.scene_description}</div>` : ''}
                        <div class="player-choice">玩家：${entry.player_choice || '无操作'}</div>
                        ${entry.npc_response ? `<div class="npc-name">NPC：${entry.npc_response}</div>` : ''}
                    </div>
                `;
            }).join('');

            // 滚动到最新的对话
            this.dialogueHistory.scrollTop = this.dialogueHistory.scrollHeight;
            
            console.log('对话历史已更新');
        } catch (error) {
            console.error('更新对话历史时出错:', error);
        }
    }

    public createModelSelector(onModelChange: (modelId: string) => void): HTMLElement {
        const container = document.createElement('div');
        container.className = 'model-selector-container';

        const label = document.createElement('div');
        label.className = 'model-selector-label';
        label.textContent = '选择AI模型:';
        container.appendChild(label);

        const optionsContainer = document.createElement('div');
        optionsContainer.className = 'model-options-container';

        MODELS.forEach((model) => {
            const option = document.createElement('div');
            option.className = 'model-option';
            if (model.id === GAME_CONFIG.defaultModel) {
                option.classList.add('active');
            }

            const modelName = document.createElement('div');
            modelName.className = 'model-name';
            modelName.textContent = model.name;

            const modelDesc = document.createElement('div');
            modelDesc.className = 'model-description';
            modelDesc.textContent = model.description;

            option.appendChild(modelName);
            option.appendChild(modelDesc);

            option.addEventListener('click', () => {
                // 移除其他选项的active类
                document.querySelectorAll('.model-option').forEach(opt => {
                    opt.classList.remove('active');
                });
                // 添加当前选项的active类
                option.classList.add('active');
                // 触发模型变更回调
                onModelChange(model.id);
            });

            optionsContainer.appendChild(option);
        });

        container.appendChild(optionsContainer);
        return container;
    }

    public createLoadGameModal(onContinue: () => void, onNewGame: () => void): void {
        // 创建模态框容器
        const modalContainer = document.createElement('div');
        modalContainer.className = 'modal-container';
        modalContainer.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h2>发现存档</h2>
                </div>
                <div class="modal-body">
                    <p>欢迎回来，赛博旅者。要继续上次的冒险，还是开启新的旅程？</p>
                    <div class="modal-buttons">
                        <button class="neon-button continue-button">
                            <span class="button-content">
                                <svg class="icon" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M8 5v14l11-7z"/>
                                </svg>
                                继续上次游戏
                            </span>
                        </button>
                        <button class="neon-button new-game-button">
                            <span class="button-content">
                                <svg class="icon" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M20 4v12h-1.5v-1.5h-1.5V16h-1.5v1.5h1.5V19h1.5v-1.5H20V20H4V4h16M6.5 7V5.5H5V7h1.5m0 1.5H5V10h1.5V8.5M9 5.5v1.5h1.5V5.5H9m1.5 3H9V10h1.5V8.5"/>
                                </svg>
                                开始新游戏
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        `;

        // 添加按钮事件监听
        const continueButton = modalContainer.querySelector('.continue-button');
        const newGameButton = modalContainer.querySelector('.new-game-button');

        if (continueButton) {
            continueButton.addEventListener('click', () => {
                modalContainer.remove();
                onContinue();
            });
        }

        if (newGameButton) {
            newGameButton.addEventListener('click', () => {
                modalContainer.remove();
                onNewGame();
            });
        }

        // 添加到页面
        document.body.appendChild(modalContainer);

        // 添加动画效果
        requestAnimationFrame(() => {
            modalContainer.classList.add('show');
        });
    }
} 
